import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  MapPin,
  Plus,
  Repeat,
  User,
  UserCheck,
  Wrench,
  X,
} from 'lucide-react';

import { usePiegeFocus } from '../hooks/usePiegeFocus';
import {
  creerTechnicien,
  lireReferencesIntervention,
  planifierIntervention,
  type Intervention,
  type Planification,
  type ReferencesIntervention,
} from '../api/interventions';

const CLASSE_CHAMP =
  'w-full pl-10 pr-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all';
const CLASSE_SELECT = `${CLASSE_CHAMP} appearance-none cursor-pointer`;
const CLASSE_ICONE = 'w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400';
const CLASSE_LIBELLE = 'block text-sm font-medium text-slate-700 mb-1.5';

interface Proprietes {
  readonly ouvert: boolean;
  readonly onFermer: () => void;
  /** Appelé après création : l'écran parent rafraîchit sa liste. */
  readonly onCree?: (intervention: Intervention) => void;
  /**
   * Jour sur lequel ouvrir le formulaire (clic sur une case du calendrier).
   * Le serveur le VALIDE et le rend dans `defauts.date` : l'écran ne l'impose pas.
   */
  readonly dateInitiale?: string;
}

/** Une étoile rouge : ce que le formulaire EXIGE, dit une fois. */
function Obligatoire() {
  return (
    <span className="text-red-600" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

/**
 * Modale « Planifier une intervention », reprise de la maquette fournie.
 *
 * Trois décisions valent d'être dites :
 * 1. **Aucune liste n'est écrite ici.** Clients, sites, types, techniciens, durées
 *    et valeurs par défaut viennent de `GET /api/interventions/references` : un
 *    `<option>` codé en dur finirait par proposer un choix que le serveur refuse.
 * 2. **L'écran n'additionne rien.** L'heure de fin affichée sous le créneau est
 *    celle que le serveur a calculée à la création ; avant l'envoi, le modal se
 *    contente d'annoncer la durée choisie.
 * 3. **Le conflit d'agenda se voit ici.** Quand le serveur répond `409`, son
 *    message (créneau occupé, client concerné) est affiché tel quel : c'est lui
 *    qui sait, pas l'écran.
 */
export function ModaleIntervention({ ouvert, onFermer, onCree, dateInitiale }: Proprietes) {
  const [references, setReferences] = useState<ReferencesIntervention>();
  const [chargement, setChargement] = useState(false);
  const [erreurListes, setErreurListes] = useState<string>();

  const [clientId, setClientId] = useState('');
  const [lieu, setLieu] = useState('');
  const [date, setDate] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [dureeMinutes, setDureeMinutes] = useState(60);
  const [typeIntervention, setTypeIntervention] = useState('');
  const [technicienId, setTechnicienId] = useState('');
  const [notes, setNotes] = useState('');
  const [recurrenceMois, setRecurrenceMois] = useState(0);

  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string>();
  /** Formulaire de création d'une fiche technicien, ouvert à la demande. */
  const [ficheOuverte, setFicheOuverte] = useState(false);
  const [ficheNom, setFicheNom] = useState('');
  const [ficheEmail, setFicheEmail] = useState('');
  const [ficheErreur, setFicheErreur] = useState<string>();
  const [ficheEnvoi, setFicheEnvoi] = useState(false);
  /** Champs vides signalés à l'écran, une fois la tentative faite. */
  const [manquants, setManquants] = useState<readonly string[]>([]);

  const premierChamp = useRef<HTMLSelectElement>(null);
  /** La boîte du modal : le clavier n'en sort pas tant qu'elle est ouverte. */
  const boite = useRef<HTMLDivElement>(null);
  usePiegeFocus(boite, ouvert);

  // Ouverture : on remet le formulaire à neuf et on demande les listes au serveur.
  useEffect(() => {
    if (!ouvert) return;
    const controleur = new AbortController();
    setChargement(true);
    setErreur(undefined);
    setErreurListes(undefined);
    setManquants([]);
    setEnvoi(false);
    void lireReferencesIntervention({ signal: controleur.signal, date: dateInitiale })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          const donnees = resultat.donnees;
          setReferences(donnees);
          // Les défauts sont ceux du SERVEUR : date du jour à Paris, heure, durée.
          setDate(donnees.defauts.date);
          setHeureDebut(donnees.defauts.heure);
          setDureeMinutes(donnees.defauts.dureeMinutes);
          setClientId('');
          setLieu('');
          setTypeIntervention('');
          setTechnicienId('');
          setNotes('');
          setRecurrenceMois(0);
          setErreur(undefined);
        } else {
          setErreurListes(resultat.message ?? 'Les listes du formulaire sont indisponibles.');
        }
        setChargement(false);
      })
      .catch(() => {
        /* abandon : la modale s'est fermée */
      });
    // Le focus va au premier champ utile, pas au bouton de fermeture.
    const minuteur = setTimeout(() => premierChamp.current?.focus(), 40);
    return () => {
      controleur.abort();
      clearTimeout(minuteur);
    };
  }, [ouvert, dateInitiale]);

  // Échap ferme : un modal sans sortie au clavier est un piège.
  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [ouvert, onFermer]);

  if (!ouvert) return null;

  const clients = references?.clients ?? [];
  const aucuneClient = !chargement && clients.length === 0;

  /** Crée la fiche technicien puis la sélectionne : une sortie, pas une impasse. */
  const ajouterTechnicien = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setFicheErreur(undefined);
    setFicheEnvoi(true);
    const resultat = await creerTechnicien({
      nom: ficheNom,
      email: ficheEmail,
      role: 'technicien',
    });
    setFicheEnvoi(false);
    if (resultat.ok && resultat.donnees) {
      // Les références sont RELUES : la liste reste la vérité du serveur.
      const rafraichies = await lireReferencesIntervention({ date: dateInitiale });
      if (rafraichies.ok && rafraichies.donnees) setReferences(rafraichies.donnees);
      setTechnicienId(resultat.donnees.id);
      setFicheOuverte(false);
      setFicheNom('');
      setFicheEmail('');
    } else {
      setFicheErreur(resultat.message ?? 'Création de la fiche refusée.');
    }
  };

  const envoyer = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setErreur(undefined);

    // Les champs obligatoires sont VÉRIFIÉS, et le refus est nommé.
    const vides: string[] = [];
    if (clientId === '') vides.push('Client');
    if (date === '') vides.push('Date');
    if (heureDebut === '') vides.push('Heure de début');
    if (typeIntervention === '') vides.push('Type d’intervention');
    setManquants(vides);
    if (vides.length > 0) {
      setErreur(`Champs obligatoires manquants : ${vides.join(', ')}.`);
      return;
    }

    const planification: Planification = {
      clientId,
      date,
      heureDebut,
      typeIntervention,
      lieu: lieu.trim(),
      dureeMinutes,
      technicienId,
      notes: notes.trim(),
      recurrenceMois,
    };

    setEnvoi(true);
    const resultat = await planifierIntervention(planification);
    setEnvoi(false);
    if (resultat.ok && resultat.donnees) {
      onCree?.(resultat.donnees);
      onFermer();
    } else {
      // Message du serveur tel quel : conflit d'agenda, champ invalide, panne.
      setErreur(resultat.message ?? 'La planification a été refusée.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-slate-900/50 p-4 overflow-y-auto"
      role="presentation"
      onMouseDown={(evenement) => {
        // Un clic sur le fond ferme ; un clic dans la boîte ne ferme pas.
        if (evenement.target === evenement.currentTarget) onFermer();
      }}
    >
      <div
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-planifier"
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-slate-200 my-8"
      >
        <div className="flex items-start gap-3 p-5 border-b border-slate-200">
          <span className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
            <CalendarDays className="w-5 h-5 text-red-600" aria-hidden="true" />
            <Plus className="w-3 h-3 text-red-600 -ml-1 mt-2" aria-hidden="true" />
          </span>
          <div className="flex-1">
            <h2 id="titre-planifier" className="text-lg font-bold text-slate-800">
              Planifier une intervention
            </h2>
            <p className="text-sm text-slate-500">
              Créez un nouveau rendez-vous sur votre planning.
            </p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer la fenêtre"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={(evenement) => void envoyer(evenement)} noValidate>
          <div className="p-5 space-y-5">
            {chargement && <p className="text-sm text-slate-500">Chargement des listes…</p>}

            {erreurListes && (
              <p
                role="alert"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
              >
                {erreurListes}
              </p>
            )}

            {aucuneClient && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Aucun client actif : créez d’abord une fiche client, sinon il n’y a rien à
                planifier.
              </p>
            )}

            {/* ── Client & lieu */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Client &amp; lieu
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-client">
                    Client
                    <Obligatoire />
                  </label>
                  <div className="relative">
                    <User className={CLASSE_ICONE} aria-hidden="true" />
                    <select
                      id="intervention-client"
                      ref={premierChamp}
                      required
                      value={clientId}
                      onChange={(evenement) => {
                        const choisi = evenement.target.value;
                        setClientId(choisi);
                        // Le lieu proposé vient de l'adresse du client (calculée
                        // par le serveur) ; une saisie déjà faite n'est pas écrasée.
                        const fiche = clients.find((client) => client.id === choisi);
                        if (lieu.trim() === '' && fiche?.lieuSuggere) {
                          setLieu(fiche.lieuSuggere);
                        }
                      }}
                      aria-invalid={manquants.includes('Client')}
                      className={CLASSE_SELECT}
                    >
                      <option value="">Sélectionnez un client…</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.nom}
                          {client.ville ? ` — ${client.ville}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-lieu">
                    Site / Localisation
                  </label>
                  <div className="relative">
                    <MapPin className={CLASSE_ICONE} aria-hidden="true" />
                    {/* Une liste fermée interdirait un nouveau site : les sites
                        connus sont proposés, un autre peut être saisi. */}
                    <input
                      id="intervention-lieu"
                      list="sites-connus"
                      value={lieu}
                      onChange={(evenement) => setLieu(evenement.target.value)}
                      placeholder="Sélectionnez un site…"
                      className={CLASSE_CHAMP}
                    />
                    <datalist id="sites-connus">
                      {(references?.sites ?? []).map((site) => (
                        <option key={site} value={site} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* ── Planification */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Planification
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-date">
                    Date
                    <Obligatoire />
                  </label>
                  <div className="relative">
                    <CalendarDays className={CLASSE_ICONE} aria-hidden="true" />
                    <input
                      id="intervention-date"
                      type="date"
                      required
                      value={date}
                      onChange={(evenement) => setDate(evenement.target.value)}
                      aria-invalid={manquants.includes('Date')}
                      className={CLASSE_CHAMP}
                    />
                  </div>
                </div>

                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-heure">
                    Heure de début
                    <Obligatoire />
                  </label>
                  <div className="relative">
                    <Clock className={CLASSE_ICONE} aria-hidden="true" />
                    <input
                      id="intervention-heure"
                      type="time"
                      required
                      value={heureDebut}
                      onChange={(evenement) => setHeureDebut(evenement.target.value)}
                      aria-invalid={manquants.includes('Heure de début')}
                      className={CLASSE_CHAMP}
                    />
                  </div>
                </div>

                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-duree">
                    Durée estimée
                  </label>
                  <div className="relative">
                    <Clock className={CLASSE_ICONE} aria-hidden="true" />
                    <select
                      id="intervention-duree"
                      value={dureeMinutes}
                      onChange={(evenement) => setDureeMinutes(Number(evenement.target.value))}
                      className={CLASSE_SELECT}
                    >
                      {(references?.durees ?? []).map((duree) => (
                        <option key={duree.minutes} value={duree.minutes}>
                          {duree.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </fieldset>

            {/* ── Détails */}
            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Détails de l’intervention
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-type">
                    Type d’intervention
                    <Obligatoire />
                  </label>
                  <div className="relative">
                    <Wrench className={CLASSE_ICONE} aria-hidden="true" />
                    <select
                      id="intervention-type"
                      required
                      value={typeIntervention}
                      onChange={(evenement) => setTypeIntervention(evenement.target.value)}
                      aria-invalid={manquants.includes('Type d’intervention')}
                      className={CLASSE_SELECT}
                    >
                      <option value="">Sélectionnez le type…</option>
                      {(references?.types ?? []).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={CLASSE_LIBELLE} htmlFor="intervention-technicien">
                    Technicien assigné
                  </label>
                  <div className="relative">
                    <UserCheck className={CLASSE_ICONE} aria-hidden="true" />
                    <select
                      id="intervention-technicien"
                      value={technicienId}
                      onChange={(evenement) => setTechnicienId(evenement.target.value)}
                      className={CLASSE_SELECT}
                    >
                      <option value="">À assigner plus tard</option>
                      {(references?.techniciens ?? []).map((technicien) => (
                        <option key={technicien.id} value={technicien.id}>
                          {technicien.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              {!ficheOuverte && (
                <button
                  type="button"
                  onClick={() => setFicheOuverte(true)}
                  className="text-xs font-medium text-red-700 hover:underline"
                >
                  + Ajouter un technicien à mon équipe
                </button>
              )}

              {ficheOuverte && (
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    Nouvelle fiche technicien — sans compte
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      aria-label="Nom du technicien"
                      value={ficheNom}
                      onChange={(evenement) => setFicheNom(evenement.target.value)}
                      placeholder="Nom complet"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                    <input
                      aria-label="Adresse du technicien"
                      type="email"
                      value={ficheEmail}
                      onChange={(evenement) => setFicheEmail(evenement.target.value)}
                      placeholder="prenom.nom@societe.fr"
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  </div>
                  {ficheErreur && (
                    <p role="alert" className="text-xs text-red-700">
                      {ficheErreur}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(evenement) => void ajouterTechnicien(evenement)}
                      disabled={ficheEnvoi}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-slate-700 rounded-lg hover:bg-slate-800 disabled:opacity-60"
                    >
                      {ficheEnvoi ? 'Création…' : 'Créer la fiche'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFicheOuverte(false);
                        setFicheErreur(undefined);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-white"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className={CLASSE_LIBELLE} htmlFor="intervention-recurrence">
                  Récurrence
                </label>
                <div className="relative">
                  <Repeat className={CLASSE_ICONE} aria-hidden="true" />
                  <select
                    id="intervention-recurrence"
                    value={recurrenceMois}
                    onChange={(evenement) => setRecurrenceMois(Number(evenement.target.value))}
                    className={CLASSE_SELECT}
                  >
                    {(references?.recurrences ?? [{ mois: 0, libelle: 'Ponctuelle' }]).map(
                      (recurrence) => (
                        <option key={recurrence.mois} value={recurrence.mois}>
                          {recurrence.libelle}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Une intervention récurrente n’engendre la suivante qu’une fois terminée.
                </p>
              </div>

              {references &&
                (references.techniciens ?? []).length === 0 &&
                !chargement &&
                !ficheOuverte && (
                  <p className="text-xs text-slate-500 flex items-start gap-1.5">
                    <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                    Aucune fiche technicien dans ce compte : l’intervention sera créée sans
                    technicien, à assigner plus tard.
                  </p>
                )}
            </fieldset>

            {/* ── Notes */}
            <div>
              <label className={CLASSE_LIBELLE} htmlFor="intervention-notes">
                Instructions / Notes (Optionnel)
              </label>
              <textarea
                id="intervention-notes"
                rows={3}
                value={notes}
                onChange={(evenement) => setNotes(evenement.target.value)}
                placeholder="Précisions pour le technicien, code d’accès, matériel à prévoir…"
                className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>

            {erreur && (
              <div
                role="alert"
                className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{erreur}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 p-5 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button
              type="button"
              onClick={onFermer}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={envoi || chargement || aucuneClient}
              className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
              {envoi ? 'Planification…' : 'Planifier l’intervention'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
